import React, { useCallback, useEffect, useState } from "react";
import styles from "./index-cta-modal.module.scss";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Button,
  Spinner,
} from "reactstrap";

import { useTranslation } from "lib/i18n/use-translation";
import IndexScoreContainer from "../../pages/woneindex/components/index-score-container";
import { useQuery } from "@apollo/client";
import appInfo from "utility/app-info";
import { useAuth } from "providers/auth";
import {
  LEARNER_SCREEN_INDEX_V1,
  LEARNER_SCREEN_INDEX_V2,
} from "utility/graphQl/query";
import { LearnerIndexCoach, LearnerIndexScreen } from "../../api/types";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import { useInViews } from "utility/useInViews";
import { useLocation } from "react-router-dom";
import { logError } from "utility/Function";

// Pinned English label for the analytics payload: the rendered CTA is
// localised, so reading it back off the DOM would fragment `button_text`.
const CTA_ANALYTICS_LABEL = "TAKE THE INDEX ASSESSMENT";

function IndexCTAModal() {
  const userID = localStorage.getItem("userID");
  const companyName = localStorage.getItem("companyName") || "NA";
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { learner } = useAuth();
  const location = useLocation();
  const { t } = useTranslation(["components"]);
  const isFromTakingAssessment = Boolean(
    location.state?.referrer === "/user/woneindex/assessment"
  );

  const {
    screens: {
      index: { version: screenVersion },
    },
  } = learner;
  const first = 1;
  const offset = 0;
  const screenQuery =
    screenVersion === "v1" ? LEARNER_SCREEN_INDEX_V1 : LEARNER_SCREEN_INDEX_V2;
  const { ref, inView } = useInViews();

  const { loading, data } = useQuery(screenQuery, {
    fetchPolicy: "cache-and-network",
    onError(error) {
      logError(
        "Error while fetching wone index data for index post session popup",
        error
      );
    },
    variables: {
      first,
      offset,
      appInfo,
    },
  });

  const screenIndex = data?.learner.screens.index as
    | LearnerIndexScreen
    | undefined;
  const coach = screenIndex?.coach;
  const score = screenIndex?.score;

  const logImpression = useCallback(
    () =>
      logAnalyticsEvent("take_assessment_popup_impression", {
        user_id: userID,
        user_id_wone: userID,
        user_company_name: companyName,
      }),
    [userID, companyName]
  );

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  useEffect(() => {
    setIsOpen(Boolean(coach?.assessmentButton?.url));
  }, [coach?.assessmentButton?.url]);

  const handleDismiss = () => {
    setIsOpen(false);
    logAnalyticsEvent("take_assessment_popup_dismissed", {
      user_id: userID,
      user_id_wone: userID,
      company_name: companyName,
    });
  };

  const handleTakeAssessment = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setIsOpen(false);
    const assessmentUrl = (
      data?.learner.screens.index.coach as LearnerIndexCoach
    ).assessmentButton?.url;

    logAnalyticsEvent("take_assessment_popup_cta_click", {
      user_id: userID,
      user_id_wone: userID,
      company_name: companyName,
      button_text: CTA_ANALYTICS_LABEL,
    });

    const win = window.open(assessmentUrl, "_self");
    win.focus();
  };

  return (
    <Modal
      className={styles["index-cta-modal"]}
      contentClassName={styles["index-cta-modal-content"]}
      isOpen={isOpen && !isFromTakingAssessment}
    >
      <ModalHeader
        className={styles["header"]}
        toggle={handleDismiss}
      ></ModalHeader>
      <ModalBody className={styles["body"]}>
        <p ref={ref}>{t("components:indexCtaModal.eyebrow")}</p>
        <h1>{t("components:indexCtaModal.title")}</h1>
        <p>{t("components:indexCtaModal.body")}</p>
        <div className={styles["index-score-container"]}>
          {loading ? (
            <Spinner />
          ) : (
            <IndexScoreContainer
              data={score}
              coach={coach}
              className="index-score-popup"
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter className={styles["footer"]}>
        <Button onClick={handleTakeAssessment}>
          {t("components:indexCtaModal.cta")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default IndexCTAModal;
